import { apiFetch } from './apiClient.js';

export type ContactStatus = 'active' | 'blocked' | 'archived';
export type NotificationConsent = 'accepted' | 'rejected' | 'unknown';

export interface Contact {
  id: string;
  tenantId: string;
  name: string;
  phone: string;
  email?: string;
  preferredChannel?: 'whatsapp' | 'webchat' | 'email' | 'sms';
  notificationConsent: NotificationConsent;
  lastContactAt?: string;
  status: ContactStatus;
  tags: string[];
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ContactCreate {
  name: string;
  phone: string;
  email?: string;
  preferredChannel?: Contact['preferredChannel'];
  notificationConsent?: NotificationConsent;
  tags?: string[];
  status?: ContactStatus;
}

export interface ListContactsParams {
  q?: string;
  notificationConsent?: NotificationConsent;
  status?: ContactStatus | 'all';
  limit?: number;
  cursor?: string;
}

export interface ListContactsResult {
  items: Contact[];
  nextCursor?: string;
}

export async function listContacts(
  tenantId: string,
  params: ListContactsParams = {},
): Promise<ListContactsResult> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.notificationConsent) qs.set('notificationConsent', params.notificationConsent);
  if (params.status) qs.set('status', params.status);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  const query = qs.toString();
  return apiFetch<ListContactsResult>(
    `/v1/tenants/${tenantId}/contacts${query ? `?${query}` : ''}`,
  );
}

export interface ImportResult {
  creados: number;
  actualizados: number;
  omitidos: number;
  duplicadosEnArchivo: number;
  errores: Array<{ linea: number; motivo: string }>;
}

/**
 * Importación masiva por CSV. El backend es tolerante: devuelve lo que entró y
 * lo que quedó fuera con su número de línea, en vez de fallar por una fila mala.
 */
export async function importContacts(tenantId: string, csv: string): Promise<ImportResult> {
  return apiFetch<ImportResult>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/contacts/import`,
    { method: 'POST', body: { csv } },
  );
}

export async function createContact(tenantId: string, body: ContactCreate): Promise<Contact> {
  return apiFetch<Contact>(`/v1/tenants/${tenantId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface ContactPatch {
  name?: string;
  phone?: string;
  email?: string;
  preferredChannel?: Contact['preferredChannel'];
  notificationConsent?: NotificationConsent;
  tags?: string[];
  status?: ContactStatus;
  expectedVersion: number;
}

export async function updateContact(
  tenantId: string,
  contactId: string,
  patch: ContactPatch,
): Promise<Contact> {
  return apiFetch<Contact>(`/v1/tenants/${tenantId}/contacts/${contactId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function archiveContact(tenantId: string, contactId: string): Promise<Contact> {
  return apiFetch<Contact>(`/v1/tenants/${tenantId}/contacts/${contactId}/archive`, {
    method: 'POST',
  });
}

export async function getContact(tenantId: string, contactId: string): Promise<Contact> {
  return apiFetch<Contact>(`/v1/tenants/${tenantId}/contacts/${contactId}`);
}
